import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, PlanView, ProgressEntry } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Avatar, EmptyState, Spinner } from "../../components/ui";
import { Envelope, FinishCeremony, LeaveNoteSheet, MomentCard, PredictionCard, PredictionsRow, useMoment, usePredictions } from "../../components/Moments";
import { Cover, authorsOf } from "../../components/BookBits";
import { CalendarIcon, CheckIcon, ChevronIcon, ClubIcon, MoreIcon, NoteIcon, SyncIcon, TalkIcon, VoteIcon } from "../../components/Icons";
import { ProgressEditor } from "../../components/ProgressEditor";
import { useClub } from "./ClubLayout";
import { MiniNotice } from "../../components/Noticeboard";
import { PickBook } from "../../components/PickBook";
import { StragglerCard } from "../../components/FinishCards";
import { DatesEditor } from "../../components/DatesEditor";
import { PlanSheet } from "../../components/PlanSheet";
import { duration, pageAt, paceLabel, type PosCtx } from "../../lib/format";
import { useClubBooks, useDiscussion, useMe } from "../../lib/queries";
import { reading, DAY, ago, coversText, countdown, dateShort, planLine, trackDetail, trackLandmark, trackPileHead, trackPosition, trackToday, trackWhen, whereParts, paceToPlan, trackCount, dayNumber } from "@pagemates/shared/copy";

const txt = reading.tab;

/*
 * The Reading tab (PLAN.md §2 Screens, redesigned 2026-11-03): the book as a
 * poster with its own cover blurred behind it, your block (where you are, the
 * pace, the editor, a note line), then everyone else on one shared track.
 * Tapping a face shows that reader under the track; faces that would overlap
 * pile up with a count, so the same track works for a club of three or three
 * hundred. The aside rows are fills, not outlined cards.
 */

export function NowTab() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useMe();
  const books = useClubBooks(clubId);
  const qc = useQueryClient();
  const linked = me.data?.hardcover.linked && me.data.hardcover.status === "ok";
  useEffect(() => {
    if (!linked) return;
    void api.hardcoverSync().then((r) => { if (r.updated > 0) void qc.invalidateQueries({ queryKey: ["books", clubId] }); }).catch(() => undefined);
  }, [linked, clubId, qc]);

  if (books.isPending) return <main className="page"><Spinner /></main>;
  if (books.isError) return <main className="page"><p className="error">{describeError(books.error)}</p></main>;
  const cur = books.data.current;
  const isAdmin = c.myRole !== "member";

  const meetingBook = books.data.current ?? books.data.finished[0] ?? null;
  const at = c.meeting ? new Date(c.meeting.at) : null;
  const tile = at && <span className="tile" aria-hidden="true"><i>{at.toLocaleDateString(undefined, { month: "short" })}</i><b>{at.getDate()}</b></span>;
  const meetingRow = (c.meeting || (isAdmin && meetingBook)) && (
    meetingBook ? (
      <Link to="/clubs/$clubId/meeting" params={{ clubId }} search={{ book: meetingBook.id }} className="quiet link">
        {tile ?? <CalendarIcon />}
        <div style={{ minWidth: 0 }}>
          <b>{at ? `${at.toLocaleDateString(undefined, { weekday: "short" })} ${at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}${c.meeting!.location ? ` · ${c.meeting!.location}` : ""}` : txt.meetingPage}</b>
          <div className="d">{c.meeting ? `${countdown(c.meeting.at)}${c.meeting.host ? txt.hosting(c.meeting.host.name) : ""}${coversText(books.data.current?.plan)}${c.meeting.rsvp.mine && !c.meeting.rsvp.mine.stale ? txt.youRe({ going: txt.going, cant: txt.notGoing, maybe: txt.aMaybe }[c.meeting.rsvp.mine.status]) : c.meeting.rsvp.mine?.stale ? txt.dateMovedStillGoing : ""}` : txt.noDateSetThe}</div>
        </div>
        <ChevronIcon className="go" />
      </Link>
    ) : (
      <div className="quiet">
        {tile}
        <div><b>{at!.toLocaleDateString(undefined, { weekday: "short" })} {at!.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}{c.meeting!.location ? ` · ${c.meeting!.location}` : ""}</b>
          <div className="d">{countdown(c.meeting!.at)}</div></div>
      </div>
    )
  );
  // The noticeboard in Around the club: the pinned notice while there is one, and what's new since your last visit.
  const board = c.board;
  const boardRows = [
    board.pinned && (
      <Link key="pin" to="/clubs/$clubId/club" params={{ clubId }} search={{ post: board.pinned.id }} className="quiet link bd-around">
        <MiniNotice />
        <div style={{ minWidth: 0 }}><b className="clip">{txt.pinned(board.pinned.firstLine)}</b><div className="d">{txt.noticeboard(board.pinned.author.name, board.pinned.until ? txt.until(new Date(board.pinned.until).toLocaleDateString(undefined, { month: "short", day: "numeric" })) : "")}</div></div>
        <ChevronIcon className="go" />
      </Link>
    ),
    board.newPosts > 0 && board.latest && (
      <Link key="new" to="/clubs/$clubId/club" params={{ clubId }} className="quiet link bd-around">
        <Avatar name={board.latest.author.name} image={board.latest.author.image} />
        <div style={{ minWidth: 0 }}><b>{txt.newOnTheNoticeboard(board.newPosts)}</b><div className="d clip">{board.latest.author.name}: {board.latest.firstLine}</div></div>
        <ChevronIcon className="go" />
      </Link>
    ),
  ].filter(Boolean);
  const straggler = books.data.straggler && <StragglerCard clubId={clubId} s={books.data.straggler} />;
  // Finished books you're still in: their own section, each row to the panel where your progress lives.
  const stillReading = books.data.finished.filter((b) => b.mine && b.mine.touched && b.mine.status === "reading" && b.mine.percent < 100 && b.id !== books.data.straggler?.clubBookId);
  const stillSection = stillReading.length > 0 && (
    <section>
      <div className="sect"><h3>{txt.stillReading}</h3><span className="count">{stillReading.length === 1 ? txt.onYourOwnTime : txt.books(stillReading.length)}</span></div>
      <div className="stack-sm" style={{ marginTop: 10 }}>
        {stillReading.map((b) => (
          <Link key={b.id} to="/clubs/$clubId/recap/$bookId" params={{ clubId, bookId: b.id }} className="quiet link">
            {b.book.coverUrl ? <span className="cover sm" style={{ width: 26, height: 39 }}><img src={b.book.coverUrl} alt="" /></span> : <TalkIcon />}
            <div style={{ minWidth: 0 }}>
              <b>{b.book.title}</b>
              <div className="d">{b.book.pages ? txt.pOf(pageAt(b.mine!.percent, b.book.pages), b.book.pages) : ""}{b.mine!.percent}%{b.finishedAt ? txt.theClubFinishedIt(dateShort(b.finishedAt)) : ""}</div>
            </div>
            <ChevronIcon className="go" />
          </Link>
        ))}
      </div>
    </section>
  );

  const vote = books.data.vote;
  // The vote row leads with the cover of whichever book is ahead.
  const leading = vote ? [...vote.entries].sort((a, b) => b.points - a.points)[0] : null;
  const leadBook = leading && leading.points > 0 ? books.data.onDeck.find((b) => b.id === (vote?.winnerId ?? leading.clubBookId)) ?? null : null;
  const voteRow = vote ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link">
      {leadBook ? <Cover book={leadBook.book} size="sm" /> : <VoteIcon />}
      <div style={{ minWidth: 0 }}><b>{vote.status === "open" ? txt.voteOnTheNext : vote.winnerId ? txt.theVoteIsIn : txt.theVoteTied}</b>
        <div className="d">{vote.status === "open"
          ? txt.of(leadBook ? txt.leads(leadBook.book.title) : "", vote.closesAt ? txt.closes(new Date(vote.closesAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })) : txt.open, vote.myRanks.length > 0 ? txt.youVeVoted : txt.youHavenTVoted, vote.voted, vote.members)
          : vote.winnerId ? `${leadBook ? `${leadBook.book.title} · ` : ""}${isAdmin ? txt.startItFromThe : txt.anAdminStartsIt}` : (isAdmin ? txt.breakTheTieOn : txt.anAdminBreaksThe)}</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : c.settings.pickPolicy === "vote" && isAdmin && (books.data.onDeck.length >= 2) && (!books.data.current || books.data.current.plan?.ended) ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link admin">
      <VoteIcon />
      <div><b>{txt.openAVoteOn}</b><div className="d">{txt.adminsTheOnDeck}</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : books.data.turn && !books.data.current ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link">
      <Avatar name={books.data.turn.user.name} image={books.data.turn.user.image} />
      <div><b>{books.data.turn.user.id === me.data?.id ? txt.itSYourTurn : txt.itSSTurn(books.data.turn.user.name)}</b><div className="d">{txt.fromTheOnDeck}</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : null;

  if (!cur) {
    // A new club's first steps sit in the side column, each gone once it's done.
    const steps = isAdmin ? [
      c.memberCount === 1 && <Link key="invite" to="/clubs/$clubId/club" params={{ clubId }} search={{ invite: true }} className="quiet link step"><ClubIcon /><div><b>{txt.itSJustYou}</b><div className="d">{txt.inviteSomeone}</div></div><ChevronIcon className="go" /></Link>,
      !c.meeting && <Link key="meet" to="/clubs/$clubId/meeting" params={{ clubId }} className="quiet link step"><CalendarIcon /><div><b>{txt.planYourFirstMeeting}</b><div className="d">{txt.aTimeAndA}</div></div><ChevronIcon className="go" /></Link>,
      !c.rules.trim() && <Link key="rules" to="/clubs/$clubId/club/settings" params={{ clubId }} className="quiet link step"><NoteIcon /><div><b>{txt.sayHowTheClub}</b><div className="d">{txt.howOftenYouMeet}</div></div><ChevronIcon className="go" /></Link>,
    ].filter(Boolean) : [];
    const around = Boolean(steps.length || voteRow || meetingRow || books.data.recentTalk || boardRows.length);
    return (
      <main className={`page now now-empty${around ? " has-aside" : ""}`}>
        <div className="now-main stack">
          <header className="club-head">
            <h1>{c.name}</h1>
            {c.description && <p className="tagline">{c.description}</p>}
          </header>
          {straggler}
          <EmptyReading clubId={clubId} isAdmin={isAdmin} canSuggest={isAdmin || c.settings.onDeckPolicy === "everyone"}
            first={books.data.finished.length === 0} deck={books.data.onDeck.length} />
          {stillSection}
        </div>
        {around && (
          <aside className="now-aside stack-sm">
            <div className="sect"><h3>{txt.aroundTheClub}</h3></div>
            {boardRows[0] /* the pinned notice is where meeting changes get seen */}
            {steps}
            {voteRow}
            {(c.meeting || !isAdmin) && meetingRow /* admins without a date get the "Plan your first meeting" step instead */}
            {books.data.recentTalk && <StillTalking clubId={clubId} t={books.data.recentTalk} />}
            {boardRows.slice(1)}
          </aside>
        )}
      </main>
    );
  }

  const mine = cur.progress.find((p) => p.id === me.data?.id);
  const finished = cur.progress.filter((p) => p.status === "finished").length;
  const showFinishCard = cur.myReader.status === "finished" && !cur.myReader.acknowledged;
  const dayN = dayNumber(cur.startedAt);
  const plan = cur.plan!;
  const ctx: PosCtx = { pages: cur.book.pages, audioSeconds: cur.book.audioSeconds, myFormat: cur.myFormat };
  const planLine = planLineFor(plan, mine?.percent ?? 0, mine?.status ?? "reading", ctx);
  const others = cur.progress.length - (mine ? 1 : 0);
  return <Reading {...{ clubId, c, me: me.data, cur, mine, finished, showFinishCard, dayN, plan, ctx, planLine, others, isAdmin, straggler, stillSection, meetingRow, boardRows, voteRow, recentTalk: books.data.recentTalk }} />;
}

/** The Reading tab with a current book. Split out so the moment and predictions can be asked for with hooks. */
function Reading({ clubId, c, me, cur, mine, finished, showFinishCard, dayN, plan, ctx, planLine, others, isAdmin, straggler, stillSection, meetingRow, boardRows, voteRow, recentTalk }: {
  clubId: string; c: import("@pagemates/shared").Club; me: import("@pagemates/shared").Me | undefined; cur: NonNullable<import("@pagemates/shared").ClubBooks["current"]>; mine: ProgressEntry | undefined; finished: number; showFinishCard: boolean;
  dayN: number | null; plan: PlanView; ctx: PosCtx; planLine: ReturnType<typeof planLineFor>; others: number; isAdmin: boolean; straggler: React.ReactNode; stillSection: React.ReactNode;
  meetingRow: React.ReactNode; boardRows: React.ReactNode[]; voteRow: React.ReactNode; recentTalk: import("@pagemates/shared").ClubBooks["recentTalk"];
}) {
  const fullCtx: PosCtx = { ...ctx, landmarks: plan.landmarks };
  const moment = useMoment(clubId, cur.id, `${mine?.percent ?? 0}:${cur.myReader.status}`);
  const preds = usePredictions(clubId, cur.id, `${mine?.percent ?? 0}:${cur.myReader.status}`);

  return (
    <main className="page now">
      <div className="now-main stack">
        {showFinishCard ? <FinishCeremony clubId={clubId} book={cur} ctx={fullCtx} moment={moment.data ?? null} p={preds.data} />
          : moment.data && <MomentCard clubId={clubId} book={cur} ctx={fullCtx} moment={moment.data} />}
        {straggler}
        <section className="hero">
          {cur.book.coverUrl && <div className="art" aria-hidden="true"><img src={cur.book.coverUrl} alt="" /></div>}
          <Cover book={cur.book} size="lg" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t">{cur.book.url ? <a href={cur.book.url} target="_blank" rel="noopener">{cur.book.title}</a> : cur.book.title}</div>
            <div className="a">{authorsOf(cur.book)}{cur.myFormat === "audio" && cur.book.audioSeconds ? ` · ${duration(cur.book.audioSeconds)}` : cur.book.pages ? txt.pages(cur.book.pages) : ""}</div>
            <div className="meta">
              {planLine ? <span className={planLine.tone}>{planLine.node}</span>
                : cur.startedAt && <span>{txt.started(dateShort(cur.startedAt))}{dayN ? <>{txt.day}<b className="n">{dayN}</b></> : null}</span>}
            </div>
          </div>
          {isAdmin && <AdminMenu clubId={clubId} book={cur} plan={plan} meetingAt={c.meeting?.at ?? null} usualPace={c.settings.usualPace} />}
        </section>

        {me && <YouBlock clubId={clubId} book={cur} mine={mine} statusAt={cur.myReader.statusAt} plan={plan} ctx={ctx} me={{ name: me.displayName ?? me.name, image: me.image }} />}
        {preds.data && <PredictionCard clubId={clubId} book={cur} ctx={fullCtx} p={preds.data} />}

        <section>
          <div className="sect"><h3>{txt.whereEveryoneElseIs}</h3><span className="count">{trackCount(others, finished)}</span></div>
          <Track entries={cur.progress} meId={me?.id} plan={plan} pages={cur.book.pages} />
        </section>
        {stillSection}
      </div>

      <div className="now-aside stack-sm">
        <div className="sect"><h3>{txt.aroundTheClub}</h3></div>
        {plan.ended && isAdmin && !c.meeting?.at && (
          <Link to="/clubs/$clubId/club" params={{ clubId }} className="quiet link admin">
            <CalendarIcon />
            <div><b>{txt.setTheNextMeeting}</b><div className="d">{txt.thePlanPicksUp}{plan.to}{txt.onceThereSA}</div></div>
            <ChevronIcon className="go" />
          </Link>
        )}
        {boardRows[0]}
        {meetingRow}
        <TalkTeaser clubId={clubId} bookId={cur.id} />
        {preds.data && <PredictionsRow ctx={fullCtx} p={preds.data} />}
        {boardRows.slice(1)}
        {voteRow}
        {recentTalk && <StillTalking clubId={clubId} t={recentTalk} />}
      </div>
    </main>
  );
}

/** The reading lamp, in the tab icons' line weight; its light is on in dark mode (see .nightstand). */
function LampArt() {
  return (
    <svg className="lamp" viewBox="0 0 96 96" aria-hidden="true">
      <path className="light" d="M30 42 L66 42 L84 92 L12 92 Z" />
      <path className="shade" d="M34 18 H62 L70 42 H26 Z" />
      <path d="M48 42 V72" /><path d="M34 78 Q48 70 62 78" /><path d="M30 92 H66" /><path d="M48 72 V92" />
    </svg>
  );
}

/** What the meeting row says about the stretch. */
/** The one line the hero says: the next landmark or the target, its date, and what's left for you (packages/shared planLine). */
function planLineFor(plan: PlanView, myPercent: number, status: string, ctx: PosCtx): { node: React.ReactNode; tone: string } | null {
  const l = planLine(plan, myPercent, status, ctx);
  if (!l) return null;
  if (l.kind === "ended") return { node: <><b>{txt.stretchEndedAtP}{l.to}</b>{l.met ?? ""}</>, tone: "" };
  return { node: <><b>{l.name}</b>{l.by} · {l.there ? txt.youReThere : <><span className="n">{l.left}</span>{txt.toGo}</>}</>, tone: "plan" };
}

/**
 * Your block: where you are and the pace on the first line, Update opens the
 * editor beneath, and the note line is the last line. Finished, given up and
 * sitting-out readers get their own first line.
 */
function YouBlock({ clubId, book, mine, statusAt, plan, ctx, me }: { clubId: string; book: ClubBook; mine: ProgressEntry | undefined; statusAt: string | null; plan: PlanView; ctx: PosCtx; me: { name: string; image: string | null } }) {
  const qc = useQueryClient();
  const percent = mine?.percent ?? 0;
  const status = mine?.status ?? "reading";
  const [open, setOpen] = useState(false);
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["discussion", clubId, book.id] }); };
  // Read it some other time: sit this one out, or come back to it.
  const rereading = useMutation({ mutationFn: (v: boolean) => api.setRereading(clubId, book.id, v), onSuccess: async () => { setOpen(false); await refresh(); } });
  const done = status === "finished", dnf = status === "dnf", before = status === "read_before";

  // The pace you'd need from here to make the next landmark (or the target) on time.
  const pace = paceToPlan(plan, percent, status, ctx);
  const w = whereParts(percent, ctx);

  return (
    <div className={`you${open ? " open" : ""}`}>
      <div className="me-row">
        <Avatar name={me.name} image={me.image} />
        <div style={{ minWidth: 0 }}>
          {done ? <div className="where"><b className="ok"><CheckIcon />{txt.finished}</b><span>{statusAt ? dateShort(statusAt) : `${percent}%`}</span></div>
            : dnf ? <div className="where"><b>{txt.stoppedAt(percent)}</b><span>{txt.doneWithItEverything}</span></div>
            : before ? <div className="where"><b>{txt.readItBefore}</b><span>{txt.sittingItOutNothing}</span></div>
            : <div className="where"><b>{w.lead}</b><span>{w.rest}{mine?.label ? ` · ${mine.label}` : ""}</span></div>}
          {!open && pace && <div className="pace">{pace}</div>}
          {!open && !pace && mine?.source === "hardcover" && <div className="pace muted"><SyncIcon width={12} height={12} />{txt.syncedFromHardcover(mine.updatedAt ? ` · ${ago(mine.updatedAt)}` : "")}</div>}
        </div>
        {before ? <button type="button" className="btn ghost sm upd" disabled={rereading.isPending} onClick={() => rereading.mutate(true)}>{txt.readingItAfterAll}</button>
          : !dnf && <button type="button" className="btn ghost sm upd" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? txt.close : txt.update}</button>}
      </div>
      {rereading.isError && <p className="error small" style={{ margin: "0 12px 8px" }}>{describeError(rereading.error)}</p>}
      {open && (
        <ProgressEditor clubId={clubId} book={book} percent={percent} myFormat={ctx.myFormat ?? (ctx.pages ? "pages" : "percent")} source={mine?.source ?? null} done={done}
          onClose={() => setOpen(false)} onSaved={() => setOpen(false)}
          sitOut={book.readBefore && book.readBefore.mine !== "unread" ? () => rereading.mutate(false) : undefined} />
      )}
      <NoteLine clubId={clubId} bookId={book.id} count={book.myNotes ?? 0} myProgress={percent} ctx={{ ...ctx, landmarks: plan.landmarks }} canForward={status === "reading" || done} />
    </div>
  );
}

/** One-line private capture, the last line of your block; the count opens the notebook. */
function NoteLine({ clubId, bookId, count, myProgress, ctx, canForward }: { clubId: string; bookId: string; count: number; myProgress: number; ctx: PosCtx; canForward: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [forward, setForward] = useState(false);
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => api.addNote(clubId, bookId, { body, position: myProgress }),
    onSuccess: async () => { setBody(""); setSaved(true); setTimeout(() => setSaved(false), 1500); await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["notes", clubId, bookId] }); },
  });
  return (
    <form className="nb-line" onSubmit={(e) => { e.preventDefault(); if (body.trim()) save.mutate(); }}>
      <NoteIcon width={16} height={16} />
      <input id="note-to-self" className="txt" value={body} maxLength={4000} placeholder={saved ? txt.saved : txt.noteToSelf} onChange={(e) => setBody(e.target.value)} aria-label={txt.noteToSelf2} />
      {body.trim() ? (
        <button type="submit" className="btn primary sm" disabled={save.isPending}>{txt.save}</button>
      ) : (
        <>
          {canForward && myProgress > 0 && <button type="button" className="fwd" aria-label={txt.leaveANoteForward} title={txt.leaveANoteForward} onClick={() => setForward(true)}><Envelope /></button>}
          <button type="button" className="cnt" onClick={() => void navigate({ to: "/clubs/$clubId/notes/$bookId", params: { clubId, bookId } })}>{count > 0 ? `${count} ${count === 1 ? txt.note : txt.notes}` : txt.notebook} ›</button>
        </>
      )}
      {forward && <LeaveNoteSheet clubId={clubId} bookId={bookId} ctx={ctx} myPercent={myProgress} onClose={() => setForward(false)} />}
      {save.isError && <span className="error small">{describeError(save.error)}</span>}
    </form>
  );
}

// ---- the track: everyone on one line

const PILE_PX = 34; // faces closer than this merge into a pile
const LIST_FIRST = 5;

/** Where a reader sits on the track: sitters-out at the start, finishers at the end. */
const posOf = trackPosition;
const isDone = (p: ProgressEntry) => p.status === "finished";

type Group = { key: string; x: number; members: ProgressEntry[] };

function Track({ entries, meId, plan, pages }: { entries: ProgressEntry[]; meId?: string; plan: PlanView; pages: number | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [sel, setSel] = useState<string | null>(null); // a group key
  const [all, setAll] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 600)); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const mine = entries.find((p) => p.id === meId) ?? null;
  // Everyone else, sorted along the track and piled where they would overlap. You stay on top at your own spot.
  const groups = useMemo<Group[]>(() => {
    const others = entries.filter((p) => p.id !== meId).sort((a, b) => posOf(a) - posOf(b) || a.name.localeCompare(b.name));
    const out: Group[] = [];
    for (const p of others) {
      const x = (posOf(p) / 100) * width;
      const last = out.at(-1);
      if (last && x - last.x < PILE_PX && isDone(p) === isDone(last.members[0]!)) { last.members.push(p); continue; }
      out.push({ key: p.id, x, members: [p] });
    }
    return out;
  }, [entries, meId, width]);
  useEffect(() => { if (sel && !groups.some((g) => g.key === sel)) setSel(null); }, [groups, sel]);
  const selected = groups.find((g) => g.key === sel) ?? null;
  const pct = (x: number) => `${(x / Math.max(1, width)) * 100}%`;
  const whenOf = trackWhen;
  const landmarkOf = (percent: number) => trackLandmark(plan, percent);

  return (
    <>
      <div className={`track${plan.todayPercent !== null ? " has-cap" : ""}`} ref={ref} aria-label={txt.whereEveryoneElseIs}>
        {mine && mine.status === "reading" && <div className="fill" style={{ width: `${mine.percent}%` }} />}
        {plan.landmarks.filter((l) => !l.beyond).map((l) => <span key={l.id} className="lm" style={{ left: `${l.percent}%` }}><b>{l.name}</b></span>)}
        {plan.todayPercent !== null && <>
          <span className="today" style={{ left: `${plan.todayPercent}%` }} title={txt.whereThePlanIs} />
          {/* Says what the tick is in plain words: the page a reader is at today if they finish right on the deadline. Kept inside the track's edges. */}
          <span className="today-cap" style={{ "--x": `${plan.todayPercent}%` } as React.CSSProperties}>{trackToday(plan.todayPercent, pages, plan.deadline)}</span>
        </>}
        {groups.map((g) => {
          const one = g.members.length === 1 ? g.members[0]! : null;
          const open = sel === g.key;
          const label = one ? `${one.name} · ${whenOf(one)}` : txt.readers2(g.members.length);
          return (
            <button key={g.key} type="button" className={`${one ? "who" : "pile"}${open ? " sel" : ""}${one && one.status === "read_before" ? " off" : ""}${g.members.every(isDone) ? " done" : ""}`}
              style={{ "--x": pct(g.x) } as React.CSSProperties} aria-label={label} aria-expanded={open} onClick={() => { setAll(false); setSel(open ? null : g.key); }}>
              {g.members.slice(0, 2).map((p) => <Avatar key={p.id} name={p.name} image={p.image} />)}
              {!one && <u>{g.members.length}</u>}
              {one && <i className="tip">{one.name}</i>}
            </button>
          );
        })}
        {mine && (
          <span className={`who me${mine.status === "read_before" ? " off" : ""}${mine.status === "finished" ? " done" : ""}`} style={{ "--x": `${posOf(mine)}%` } as React.CSSProperties} title={txt.you}>
            <Avatar name={mine.name} image={mine.image} />
          </span>
        )}
      </div>
      {selected && selected.members.length === 1 && (() => { const p = selected.members[0]!;
        const detail = trackDetail(p, pages, plan);
        return (
        <div className="sel-row">
          <Avatar name={p.name} image={p.image} />
          <div style={{ minWidth: 0 }}>
            <div className="n">{p.name}</div>
            <div className="d">{detail}</div>
          </div>
        </div>
      ); })()}
      {selected && selected.members.length > 1 && (() => {
        const ms = [...selected.members].sort((a, b) => posOf(b) - posOf(a));
        const shown = all ? ms : ms.slice(0, LIST_FIRST);
        const done = ms.every(isDone);
        const lo = Math.min(...ms.map(posOf)), hi = Math.max(...ms.map(posOf));
        const head = trackPileHead(ms.length, done, lo, hi, pages);
        const lm = done ? null : landmarkOf(hi);
        return (
          <div className="sel-list">
            <div className="hd"><span>{head}</span>{lm && <span>{txt.past(lm)}</span>}</div>
            {shown.map((p) => (
              <div key={p.id} className="m"><Avatar name={p.name} image={p.image} /><span style={{ minWidth: 0 }}>{p.name}<span className="s">{whenOf(p)}</span></span><span className="p">{p.status === "finished" || p.status === "read_before" ? <CheckIcon /> : p.status === "dnf" ? "—" : `${p.percent}%`}</span></div>
            ))}
            {!all && ms.length > LIST_FIRST && <button type="button" className="more" onClick={() => setAll(true)}>{txt.more(ms.length - LIST_FIRST)}</button>}
          </div>
        );
      })()}
    </>
  );
}

function AdminMenu({ clubId, book, plan, meetingAt, usualPace }: { clubId: string; book: ClubBook; plan: PlanView; meetingAt: string | null; usualPace: number | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<"finish" | "shelve" | "dates" | "plan" | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["books", clubId] });
  const finish = useMutation({ mutationFn: () => api.setBookStatus(clubId, book.id, "finished"), onSuccess: refresh });
  const shelve = useMutation({ mutationFn: () => api.setBookStatus(clubId, book.id, "on_deck"), onSuccess: refresh });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="hero-menu" ref={ref}>
      <button type="button" className="more" aria-label={txt.bookActions} aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreIcon /></button>
      {open && (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("finish"); }}>{txt.finishThisBook}</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("shelve"); }}>{txt.putBackOnDeck}</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("dates"); }}>{txt.editStartDate}</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("plan"); }}>{plan.ended ? txt.planThisStretchFrom(plan.to) : plan.custom ? txt.editThePlan : txt.planTheRead}</button>
        </div>
      )}
      {confirm === "plan" && <PlanSheet clubId={clubId} book={book} plan={plan} meetingAt={meetingAt} usualPace={usualPace} onClose={() => setConfirm(null)} />}
      {confirm === "dates" && (
        <div className="hero-confirm" style={{ padding: 0, border: 0 }}>
          <DatesEditor clubId={clubId} book={book} onDone={() => setConfirm(null)} />
        </div>
      )}
      {confirm && confirm !== "dates" && confirm !== "plan" && (
        <div className="confirm hero-confirm">
          <p className="small">{confirm === "finish" ? txt.moveToFinishedEveryone(book.book.title) : txt.shelveItForNow}</p>
          <div className="row">
            <button className="btn primary sm" disabled={finish.isPending || shelve.isPending} onClick={() => (confirm === "finish" ? finish : shelve).mutate()}>{txt.yes}</button>
            <button className="btn ghost sm" onClick={() => setConfirm(null)}>{txt.cancel}</button>
          </div>
          {(finish.isError || shelve.isError) && <p className="error">{describeError(finish.error ?? shelve.error)}</p>}
        </div>
      )}
    </div>
  );
}

function StillTalking({ clubId, t }: { clubId: string; t: import("@pagemates/shared").RecentTalk }) {
  return (
    <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: t.clubBookId }} className="quiet link">
      {t.coverUrl ? <span className="cover sm" style={{ width: 26, height: 39 }}><img src={t.coverUrl} alt="" /></span> : <TalkIcon />}
      <div style={{ minWidth: 0 }}>
        <b>{txt.stillTalkingAbout(t.title)}</b>
        <div className="d">{txt.last(t.unread > 0 ? txt.new(t.unread) : "", ago(t.lastAt))}</div>
      </div>
      <ChevronIcon className="go" />
    </Link>
  );
}

function TalkTeaser({ clubId, bookId }: { clubId: string; bookId: string }) {
  const d = useDiscussion(clubId, bookId, { staleTime: 30_000 });
  if (!d.data) return null;
  const t = d.data;
  const last = [...t.comments].reverse().find((c) => !c.removed);
  const lastAt = last ? (last.lastReplyAt && last.lastReplyAt > last.createdAt ? last.lastReplyAt : last.createdAt) : null;
  return (
    <Link to="/clubs/$clubId/talk" params={{ clubId }} className="quiet link">
      <TalkIcon />
      <div>
        <b className={t.total === 0 ? undefined : "n"}>{t.total === 0 ? txt.noMessagesYet : `${t.total} ${t.total === 1 ? txt.message : txt.messages}`}</b>
        <div className="d">{t.total === 0 ? txt.startTheConversation : `${t.lockedCount > 0 ? txt.sealedAheadOfYou(t.lockedCount) : txt.nothingSealedForYou}${lastAt ? txt.last2(ago(lastAt)) : ""}`}</div>
      </div>
      <ChevronIcon className="go" />
    </Link>
  );
}

/**
 * Nothing being read: the lamp over one empty-state card. Admins pick the
 * book straight from the catalog (it starts at once, whatever the club's
 * picking method); members suggest one, or wait when only admins can.
 */
function EmptyReading({ clubId, isAdmin, canSuggest, first, deck }: { clubId: string; isAdmin: boolean; canSuggest: boolean; first: boolean; deck: number }) {
  const [picking, setPicking] = useState(false);
  const lamp = <div className="es-lamp"><div className="glow" aria-hidden="true" /><LampArt /></div>;
  const onDeckLink = deck > 0
    ? <Link to="/clubs/$clubId/shelf" params={{ clubId }}>{txt.orChooseFromThe(deck)}</Link>
    : null;
  if (isAdmin) {
    return (
      <>
        <EmptyState className="nightstand" art={lamp} title={txt.nothingOnTheNightstand}
          action={<button type="button" className="btn primary" onClick={() => setPicking(true)}>{first ? txt.pickTheFirstBook : txt.pickTheNextBook}</button>}
          secondary={onDeckLink}>
          {first ? txt.pickTheClubS : txt.pickTheClubS2}
        </EmptyState>
        {picking && <PickBook clubId={clubId} mode="current" first={first} onClose={() => setPicking(false)} />}
      </>
    );
  }
  return (
    <EmptyState className="nightstand" art={lamp} title={first ? txt.waitingForTheFirst : txt.waitingForTheNext}
      action={canSuggest ? <Link to="/clubs/$clubId/suggestions" params={{ clubId }} search={{ add: true }} className="btn primary">{txt.suggestABook}</Link> : undefined}>
      {canSuggest ? txt.whenAnAdminPicks : txt.anAdminWillPick}
    </EmptyState>
  );
}
